import { parseServerTimestamp } from "./traceUtils.js";
// The response to the focal request, as a turn.
//
// A captured trace is a REQUEST body: it carries the whole conversation up to
// and including the message that asked, and nothing after. Every assistant
// turn a trace shows today was recovered from a LATER request's echo of it —
// which is exactly why the last reply of a session, or any reply whose
// follow-up request was never captured, has been invisible. The normalized
// conversation model (2026-08-12) stores observed responses directly, so the
// missing half can now be fetched and appended as a turn.
//
// Appended at the END, deliberately. The turn axis is positional, and every
// consumer of it (lanes, beats, influence edges, the review strip) indexes
// into the array it was handed — inserting anywhere else renumbers turns that
// other readings already point at. The response genuinely is the last thing
// that happened in this trace, so the end is also the honest position.

// One block per response part, in the trace turn-block vocabulary
// (parseApiRequest's shapes), so every existing reader — readTurn, the
// semantic feed, the lanes reply beat — takes it without special cases.
export function responseTurnFromParts(parts = [], { at = null, capture = null, completeness = null, messageId = null, sourceEventId = null } = {}) {
  const blocks = [];
  for (const p of parts || []) {
    const kind = String(p?.kind || "");
    const text = typeof p?.text === "string" ? p.text : "";
    if (kind === "tool_use" && p?.body?.name) {
      // The calls the model made ARE the response for a tool-driven turn —
      // for a hosted code-mode agent this is the cell's literal source,
      // which never appears in any request body (stored conversations echo
      // nothing). The block shape is parseApiRequest's, so every existing
      // reader renders it.
      blocks.push({ type: "tool_use", id: p.body.id ?? null, name: p.body.name, input: p.body.input ?? {},
        ...(p.body.typedInput ? { typedInput: p.body.typedInput } : {}) });
      continue;
    }
    if (!text) continue;
    if (kind === "reasoning") blocks.push({ type: "thinking", thinking: text });
    else blocks.push({ type: "text", text });
  }
  if (!blocks.length) return null;
  return {
    role: "assistant",
    label: "Captured response",
    // The flag every renderer keys "this was observed, not echoed" copy on.
    captured: true,
    capturedAt: at, messageId, observationSourceEventId: sourceEventId,
    messageAt: messageId ? parseServerTimestamp(at) : null,
    captureSource: capture,
    captureCompleteness: completeness,
    blocks,
  };
}

// Append the captured response to a trace's turns.
//
// Skips are as important as the append:
//   - no captured turn → the turns pass through UNTOUCHED (same reference), so
//     memoized consumers don't rebuild for a fetch that found nothing;
//   - a trailing assistant turn already carrying the same text → skip, because
//     a retried request can echo the response the model just gave, and showing
//     it twice claims two replies where there was one;
//   - a turn already carrying the response's message -> skip. A session's record
//     holds every observed message, so the response is often on the axis
//     already, and one made only of tool calls has no text for the check
//     above to match. Appended again, its calls (and any finding placed on
//     them by message id) move onto a copy the clock cannot place.
export function appendCapturedResponse(turns = [], capturedTurn = null) {
  const base = Array.isArray(turns) ? turns : [];
  if (!capturedTurn?.blocks?.length) return base;
  const capturedId = capturedTurn.messageId ? String(capturedTurn.messageId).toLowerCase() : "";
  if (capturedId && base.some((t) => String(t?.messageId || "").toLowerCase() === capturedId)) return base;
  const capturedText = turnText(capturedTurn);
  // Only a TRAILING assistant turn can be this response's echo — an assistant
  // turn behind a later user message is a previous reply, identical text or
  // not. Trailing system turns (the synthetic "Tools offered" appendix) are
  // stepped over: they are bookkeeping, not conversation.
  let i = base.length - 1;
  while (i >= 0 && base[i]?.role === "system") i -= 1;
  if (i >= 0 && base[i]?.role === "assistant" && capturedText && turnText(base[i]) === capturedText) {
    return base;
  }
  return [...base, capturedTurn];
}

function turnText(turn) {
  return (turn?.blocks || [])
    .map((b) => (b?.type === "text" ? b.text : b?.type === "thinking" ? b.thinking : ""))
    .join("\n")
    .trim();
}
